import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import { AutoSizer, List, InfiniteLoader } from 'react-virtualized';

import { useDispatch, useSelector } from 'react-redux';
import { fetchSearchResultsAsync, setIsOwnershipResultsExpanded } from '../../../../../redux/ownership/Actions';
import { highlightFeatures, unhighlightFeatures } from '../../../../../redux/highlights/Actions';
import { setFitBounds } from '../../../../../redux/map/Actions';
import { MAIN_MAP } from '../../../../../redux/map/Constants';

import {
    CircularProgress,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Typography,
    makeStyles,
    Button
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import ScrollableContainer from '../../../../utility/ScrollableContainer';
import SimpleSelect from '../../common/SimpleSelect';
import CreateSiteCardDialog from '../../../../Common/SiteCard/CreateDialog';
import ResultCard from './ResultCard';

const useStyles = makeStyles((theme) => style(theme));
const BATCH_SIZE = 25;

function ResultList () {
    const classes = useStyles();
    const dispatch = useDispatch();
    function dispatchFetchSearchResults (id, start, end) { dispatch(fetchSearchResultsAsync(id, start, end)); }
    function dispatchSetIsResultsExpanded () { dispatch(setIsOwnershipResultsExpanded(false)); }
    function dispatchHighlightFeature (highlight) { dispatch(highlightFeatures([highlight])); }
    function dispatchUnhighlightFeature (highlight) { dispatch(unhighlightFeatures([highlight])); }
    function dispatchSetFitBounds (bounds, options = {}) { dispatch(setFitBounds(bounds, options, MAIN_MAP)); }
    const layerId = useSelector(state => state.ownershipReducer.ownershipSearchArea.layerId);
    const layer = useSelector(state => state.layers.layers.find(layer => layer.id === layerId));
    const siteCards = useSelector(state => state.siteCard.siteCards);
    const search = useSelector(state => state.ownershipReducer.search);
    const results = useSelector(state => state.ownershipReducer.results);
    const selectedResults = useSelector(state => []);
    const isLoadingResults = useSelector(state => state.ownershipReducer.isLoadingResults);

    const totalRows = search.computedData.length;
    const currentRow = results.length;
    const selectedResultsLength = selectedResults.length;

    const [createSiteCardResult, setCreateSiteCardResult] = useState(null);

    function openCreateSiteCardDialog (result) {
        setCreateSiteCardResult(result);
    }

    function closeCreateSiteCardDialog () {
        setCreateSiteCardResult(null);
    }

    function handleResultCardEnter (featureId) {
        dispatchHighlightFeature({
            key: `search_${search.id}_polygons`,
            layerSystemName: layer.system_name,
            features: [{
                featureId: featureId
            }],
            styles: [
                {
                    'type': 'line',
                    'paint': {
                        'line-color': '#A9FE95',
                        'line-width': 10
                    },
                    'maxZoom': 22,
                    'minZoom': 12,
                }
            ]
        });
        dispatchHighlightFeature({
            key: `search_${search.id}_points`,
            layerSystemName: layer.system_name,
            features: [{
                featureId: featureId
            }],
            styles: [
                {
                    'type': 'circle',
                    'paint': {
                        'circle-radius': 6.5,
                        'circle-color': 'rgba(0,0,0,0)',
                        'circle-stroke-width': 3,
                        'circle-stroke-color': '#A9FE95'
                    },
                    'maxZoom': 12,
                    'minZoom': 1,
                }
            ]
        });
    }

    function handleResultCardLeave (featureId) {
        dispatchUnhighlightFeature({
            key: `search_${search.id}_polygons`,
            layerSystemName: layer.system_name
        });
        dispatchUnhighlightFeature({
            key: `search_${search.id}_points`,
            layerSystemName: layer.system_name
        });
    }

    function handleZoomFeature (feature) {
        // const coords = JSON.parse(feature.bounds).coordinates;
        const coords = feature.bounds.coordinates;
        dispatchSetFitBounds([coords[0][2], coords[0][0]], {
            pitch: 0,
            bearing: 0,
            maxZoom: 16
        }); 
    }

    const throttledLoadNextPage = useCallback(
        throttle(
            ({ startIndex, stopIndex }) => {
                loadNextPage({ startIndex, stopIndex });
            },
            500),
        [loadNextPage]
    );

    function loadNextPage ({ startIndex, stopIndex }) {
        if (isLoadingResults) {
            return;
        }
        if(currentRow < totalRows && (stopIndex - 15 > currentRow - 2 || stopIndex === totalRows - 1)) {
            console.log('Loading', currentRow, stopIndex, totalRows); 
            // Round up to nearest 25 just to keep numbers nice   
            const to = Math.ceil(stopIndex / BATCH_SIZE) * BATCH_SIZE;   
            dispatchFetchSearchResults(search.id, currentRow, to);
        }
    }

    function isRowLoaded ({index}) {
        index < results.length;
    }

    function rowRenderer ({ index, key, style }) {
        if (index < results.length) {
            const ownership = results[index];
            const siteCard = siteCards.find(sc => sc.featureId === ownership.polygonId && sc.layerId === 159);
            return (
                <div key={key} style={style}>
                    <ResultCard
                        ownership={ownership}
                        siteCard={siteCard}
                        onSaveClick={openCreateSiteCardDialog}
                        onHoverEnter={handleResultCardEnter}
                        onHoverLeave={handleResultCardLeave}
                        onZoomFeature={handleZoomFeature}
                    />
                </div>
            );
        }
        return (
            <div key={key} style={{...style}}>
                <Skeleton animation="wave" height={60} variant="rect" />
            </div>
        ); 
    }

    function lnp ({startIndex, stopIndex}) {
        throttledLoadNextPage({startIndex, stopIndex});
    } 

    function handleAllSelected (event) {
        // this.props.dispatchSetAllFeaturesSelected(event.target.checked);
    }

    // Make sure the scroll bar gradually expands so user can't go straight to the end and load everything
    const rowCount = totalRows === currentRow ? totalRows : Math.min(totalRows, currentRow + BATCH_SIZE);

    return (
        <div className={classes.root}>
            {
                Boolean(search && search.status === 'complete') &&
                <>
                    { 
                        Boolean(createSiteCardResult) && 
                        <CreateSiteCardDialog
                            open={Boolean(createSiteCardResult)}
                            onClose={closeCreateSiteCardDialog}
                            featureConfig={{
                                layerId: 159,
                                featureId: createSiteCardResult.polygon_id
                            }}
                            defaultName={createSiteCardResult.proprietor_name}
                        />
                    }
                    <div className={classes.flex}>
                        <Typography color="textPrimary" variant="subtitle1">
                            Results ({currentRow}/{totalRows})
                        </Typography>
                        <Button
                            classes={{ root: classes.backButton }}
                            onClick={dispatchSetIsResultsExpanded}
                        >
                            Back
                        </Button>
                    </div>
                    <InfiniteLoader 
                        isRowLoaded={isRowLoaded}
                        loadMoreRows={lnp}
                        minimumBatchSize={10}
                        rowCount={rowCount}
                        threshold={15}
                    >
                        {({ onRowsRendered, registerChild }) => (
                            <List
                                ref={registerChild}
                                onRowsRendered={onRowsRendered}
                                rowRenderer={rowRenderer}

                                // overscanRowCount={2}
                                className={classes.scrollable}
                                height={500}
                                rowCount={rowCount}
                                rowHeight={70}
                                estimatedRowSize={70}
                                width={500}
                            />
                        )}
                    </InfiniteLoader>
                </>
            }
            {
                Boolean(search && ['running', 'queued', 'ready'].includes(search.status)) &&
                <CircularProgress />
            }
        </div>
    );
}

const style = theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        // flexGrow: 1,
        // overflowX: 'hidden',
        // overflowY: 'hidden',
        // maxHeight: 300,
        // minHeight: 300
    },
    flex: {
        display: 'flex',
        alignItems: 'center',
        '& > *:first-child': {
            marginRight: 'auto',
            marginLeft: 0
        }
    },
    scrollable: {
        ...theme.components.scrollable,
        '&::-webkit-scrollbar': {
            width: 16
        },
        '&::-webkit-scrollbar-thumb': {
            background: theme.palette.grey.variant3,
            borderLeft: `8px ${theme.palette.background.main} solid`,
            backgroundClip: 'padding-box'
        }
    },
    cards: {
        '&> *:not(:last-child)':{
            marginBottom: theme.constants.verticalMargin
        },
        '&::-webkit-scrollbar': {
            width: 16
        },
        '&::-webkit-scrollbar-thumb': {
            background: theme.palette.grey.variant3,
            borderLeft: `8px ${theme.palette.background.main} solid`,
            backgroundClip: 'padding-box'
        }
    },
    backButton: {
        ...theme.typography.caption,
        color: theme.palette.primary.light,
        textTransform: 'initial',
        margin: '-6px -8px',
        justifyContent: 'end',
        minWidth: 0
    },
});

export default ResultList;